import React, { useEffect, useRef, useState } from "react";
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography } from '@material-ui/core';
import { useQuestionChat } from '../../useContext/QuestionChatProvider';
import { useSnackbar } from 'notistack';
import { Tooltip } from '../../toolympus/components/primitives/Tooltip';
import { FormattedMessage, useIntl } from 'react-intl';
import { Time } from './Questions.style';
import { useFormats } from '../../toolympus/components/schemed';
import { utc } from '../../toolympus/components/timezone';
import { FeedEvent, Question } from '../../api/types/main';
import { NotificationSettingsPopup } from './NotificationSettings/NotificationSettingsPopup';
import { useNotificationSettings } from './NotificationSettings/useNotificationSettings';
import { downloadFile } from '../../toolympus/api/core';
import { openFileUploader } from '../../toolympus/api/files';
import { Buttons, LoadingIndicator } from '../../toolympus/components/primitives';
import { ChatAttachmentFileApiPath, useCreateAttachment } from './useCreateAttachment';
import { StoredMessage } from './hook/types';
import { ChatRecentMessagesDialog } from './ChatRecentMessagesDialog';
import {
  AdminMessage,
  MessageHeadLine,
  TextLink,
  UserName,
  Message,
  Text,
  Wrapper,
  Messages,
  UserInput,
  NotificationsIcon,
  AttachmentIcon,
  InputMessageWrapper,
  SendIconWrapper,
  HistoryIconWrapper,
  AttachmentFileIcon,
  WarningIcon,
  AdminOfficeIcon,
} from './QuestionChatStyles';

interface Props {
    question: Question;
}

const ChatItem = (props: { message: FeedEvent }) => {
  const { message } = props;
  const { formatDatetimeShort, formatDatetime } = useFormats();

  if(message.isEvent) {
    const messageText = message.text || message.display_text;
    if(!messageText || message.event_type === "question-launched") {
      return null;
    }

    return (
      <AdminMessage isWarning={message.isWarning}>
        <MessageHeadLine side={"left"}>
          <UserName>
            <AdminOfficeIcon />
            <FormattedMessage id="chat.admin_office" />
            {message.isWarning && <WarningIcon />}
          </UserName>
          <div className="occupy" />
          <Tooltip text_id={formatDatetime(utc.toLocal(message.time))}>
            <Time>{formatDatetimeShort(utc.toLocal(message.time))}</Time>
          </Tooltip>
        </MessageHeadLine>

        <Text>{messageText}</Text>
      </AdminMessage>
    )
  } else {
    return (
      <Message isMy={message.isMyMessage}>
        <MessageHeadLine side={message.isMyMessage ? "right" : "left"}>
          {!message.isMyMessage && <UserName>{message.sender}</UserName>}
          {!message.isMyMessage && <div className="occupy" />}
          <Tooltip text_id={formatDatetime(utc.toLocal(message.time))}>
            <Time>{formatDatetimeShort(utc.toLocal(message.time))}</Time>
          </Tooltip>
        </MessageHeadLine>

        {message.attachment_file_id
          ? (
            <TextLink onClick={() => downloadFile(`${ChatAttachmentFileApiPath}/${message.attachment_file_id}`, message.text)}>
              <AttachmentFileIcon size="small" /> {message.text}
            </TextLink>
          )
          : (
            <Text>{message.text}</Text>
          )}
            
      </Message>
    )
  }
}




export const QuestionChat = (props: Props) => {
  const [newMessage, setNewMessage] = useState("");
  const { questionChat, sendMessage, recentMessages, isOpen: isFeedSocketOpen } = useQuestionChat();
  const { enqueueSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const messages = useRef<HTMLDivElement>(null);

  const notificationSettings = useNotificationSettings(props.question);
  const attachment = useCreateAttachment(sendMessage);

  const doSendMessage = () => {
    if(newMessage.length <= 0) {
      enqueueSnackbar(formatMessage({ id: "chat.message_empty" }), { variant: "error", autoHideDuration: 2000 })
    } else {
      const result = sendMessage(newMessage);
      if(result) {
        setNewMessage("");
      } else {
        enqueueSnackbar(formatMessage({ id: "chat.message_failed" }), { variant: "error", autoHideDuration: 2000 })
      }
    }
  }

  useEffect(() => {
      (Array.from(messages.current?.children as HTMLCollection | undefined[])
          .slice(-1)[0] as HTMLElement)?.scrollIntoView(({ behavior: "smooth" }));
  }, [ questionChat.length ]);

  
  const [showHistory, setShowHistory] = useState<StoredMessage[] | null>(null);


  return (
    <Wrapper>
      {!isFeedSocketOpen &&
        <div className="socket-openning">
          <Typography variant="caption"><FormattedMessage id="chat.reconnecting" /></Typography>
          <LoadingIndicator sizeVariant="font" className="spinner" />
        </div>}
      
      <Messages ref={messages}>
        {!questionChat?.length && formatMessage({ id: "chat.no_messages" })}
        {questionChat.map((message: FeedEvent) => (
          <ChatItem key={message._id} message={message} />
        ))}
      </Messages>


      <UserInput>
        <NotificationsIcon onClick={notificationSettings.dialog.open} />
        <AttachmentIcon onClick={() => openFileUploader(f => attachment.saveFile(f))} />
        <InputMessageWrapper
            onChange={e => setNewMessage(e.target.value)}
            value={newMessage}
            onKeyUp={(e) => e.ctrlKey && e.key === "Enter" && doSendMessage()}
            placeholder={formatMessage({ id: "chat.message_placeholder" })}
            />
        
        {!!recentMessages?.length && <HistoryIconWrapper onClick={() => setShowHistory(recentMessages)} />}
        <SendIconWrapper onClick={() => doSendMessage()} />
      </UserInput>


      <NotificationSettingsPopup data={notificationSettings} />

      <Dialog fullWidth maxWidth="sm" open={!!attachment.activeFile} onClose={() => attachment.cancel()}>
        <DialogTitle><FormattedMessage id="chat.send_file_to_chat" /></DialogTitle>
        <DialogContent>
          <Buttons>
            <AttachmentFileIcon />
            <Typography>{attachment.activeFile?.filename}</Typography>
          </Buttons>
        </DialogContent>
        <DialogActions>
            <Button onClick={() => { attachment.cancel(); }}>
                <FormattedMessage id="common.cancel" />
            </Button>
            <Button variant="contained" color="primary" onClick={() => { attachment.complete(); }}>
                <FormattedMessage id="chat.send" />
            </Button>
        </DialogActions>
      </Dialog>

      <ChatRecentMessagesDialog
        messages={showHistory}
        onClose={() => setShowHistory(null)}
        onSelect={m => setNewMessage(newMessage?.trim()?.length ? `${newMessage}\n${m.text}` : m.text)}
        />

    </Wrapper>)
}
